import { useState } from "react";
import { Flex, Text } from "rebass/styled-components";
import styled, { keyframes } from "styled-components";

import { getTagBadges, TTagBadge } from "MVVM/Utilities/ClipTags";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";

type TBadgeSize = "sm" | "md";

const SIZES = {
  sm: { fontSize: 12, padding: "4px 9px", iconSize: 14, weaponIcon: 16 },
  md: { fontSize: 13, padding: "5px 12px", iconSize: 16, weaponIcon: 18 },
} as const;

interface IClipBadgesProps {
  tags?: { key: string; value: string }[];
  size?: TBadgeSize;
  maxVisible?: number;
  expandable?: boolean;
}

const ClipBadges = ({
  tags,
  size = "sm",
  maxVisible = 2,
  expandable = false,
}: IClipBadgesProps) => {
  const [showAll, setShowAll] = useState(false);
  const tagBadges = getTagBadges(tags);

  if (tagBadges.length === 0) return null;

  const visibleBadges = showAll ? tagBadges : tagBadges.slice(0, maxVisible);
  const hasMore = tagBadges.length > maxVisible;

  return (
    <Flex
      alignItems="center"
      style={{ gap: "6px", flexWrap: expandable ? "wrap" : undefined }}
    >
      {visibleBadges.map((badge, i) => (
        <ClipBadge key={i} badge={badge} size={size} />
      ))}
      {expandable && hasMore && (
        <ViewAllButton onClick={() => setShowAll(!showAll)}>
          {showAll ? "Show Less" : `+${tagBadges.length - maxVisible} More`}
        </ViewAllButton>
      )}
    </Flex>
  );
};

export { ClipBadges };

interface IClipBadgeProps {
  badge: TTagBadge;
  size: TBadgeSize;
}

const getBadgeWrapper = (badge: TTagBadge) => {
  switch (true) {
    case badge.ace:
      return AceBadgeStyled;
    case badge.highlight:
      return HighlightBadgeStyled;
    case badge.featured:
      return FeaturedBadgeStyled;
    default:
      return null;
  }
};

const ClipBadge = ({ badge, size }: IClipBadgeProps) => {
  const Icon = badge.icon;
  const Wrapper = getBadgeWrapper(badge);
  const iconSize = SIZES[size].iconSize;

  const content = Wrapper ? (
    <Wrapper $size={size}>
      <Icon size={iconSize} />
      {badge.label}
    </Wrapper>
  ) : (
    <TTagBadgePill $size={size}>
      {badge.iconUrl ? (
        <WeaponIcon $size={size} src={badge.iconUrl} alt="" />
      ) : (
        <Icon size={iconSize} />
      )}
      {badge.label}
    </TTagBadgePill>
  );

  if (badge.tooltip) {
    return <AnimatedTooltip content={badge.tooltip}>{content}</AnimatedTooltip>;
  }
  return content;
};

const shimmer = keyframes`
  0% { background-position: -100px 0; }
  100% { background-position: 200px 0; }
`;

const TTagBadgePill = styled(Text)<{ $size: TBadgeSize }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy50};
  border: 1px solid transparent;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.shade200};
  display: inline-flex;
  font-family: Barlow;
  font-size: ${({ $size }) => SIZES[$size].fontSize}px;
  font-weight: 600;
  gap: 4px;
  letter-spacing: 0.5px;
  line-height: 1;
  padding: ${({ $size }) => SIZES[$size].padding};
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 2px 6px;
  }
`;

const AceBadgeStyled = styled(Text)<{ $size: TBadgeSize }>`
  align-items: center;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.goldStar},
    ${({ theme }) => theme.colors.timelineGold}
  );
  border: none;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.navy900};
  display: inline-flex;
  font-family: Barlow;
  font-size: ${({ $size }) => SIZES[$size].fontSize}px;
  font-weight: 800;
  gap: 4px;
  letter-spacing: 0.8px;
  line-height: 1;
  overflow: hidden;
  padding: ${({ $size }) => SIZES[$size].padding};
  position: relative;
  text-shadow: 0 1px 2px rgba(255, 215, 0, 0.2);
  text-transform: uppercase;
  white-space: nowrap;

  &::after {
    animation: ${shimmer} 5s ease-in-out infinite;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 100%
    );
    content: "";
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 80px;
  }

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 2px 6px;
  }
`;

const FeaturedBadgeStyled = styled(Text)<{ $size: TBadgeSize }>`
  align-items: center;
  background: ${({ theme }) => theme.colors.envy}20;
  border: 1px solid ${({ theme }) => theme.colors.envy}40;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.envy};
  display: inline-flex;
  font-family: Barlow;
  font-size: ${({ $size }) => SIZES[$size].fontSize}px;
  font-weight: 600;
  gap: 4px;
  letter-spacing: 0.5px;
  line-height: 1;
  padding: ${({ $size }) => SIZES[$size].padding};
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 10px;
    padding: 2px 6px;
  }
`;

const HighlightBadgeStyled = styled(FeaturedBadgeStyled)`
  overflow: hidden;
  position: relative;

  &::after {
    animation: ${shimmer} 5s ease-in-out infinite;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(77, 189, 148, 0.15) 50%,
      transparent 100%
    );
    content: "";
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 200%;
  }
`;

const WeaponIcon = styled.img<{ $size: TBadgeSize }>`
  filter: brightness(0) invert(0.75);
  height: ${({ $size }) => SIZES[$size].weaponIcon}px;
  margin: -1px 0;
  object-fit: contain;
  width: ${({ $size }) => SIZES[$size].weaponIcon}px;
`;

const ViewAllButton = styled.button`
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.navy200};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.shade300};
  cursor: pointer;
  font-family: Barlow;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  padding: 5px 12px;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
    color: ${({ theme }) => theme.colors.envy};
  }
`;
